import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
    });

    // 请求拦截器：添加 token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // 响应拦截器：处理 401
    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        if (error.response?.status === 401) {
          const originalRequest = error.config;
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            const refreshed = await this.tryRefreshToken();
            if (refreshed) {
              return this.client(originalRequest);
            }
          }
          // 清除 token 并跳转登录（超时登出也清客服未读轮询标志）
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('support_contacted');
          window.location.href = '/login?reason=session_expired';
        }
        return Promise.reject(error.response?.data || error);
      }
    );
  }

  private async tryRefreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken });
      if (response.data.success) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        return true;
      }
    } catch {
      // refresh 失败
    }
    return false;
  }

  async get<T = any>(url: string): Promise<T> {
    return this.client.get(url) as Promise<T>;
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    return this.client.post(url, data) as Promise<T>;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    return this.client.put(url, data) as Promise<T>;
  }

  async delete<T = any>(url: string): Promise<T> {
    return this.client.delete(url) as Promise<T>;
  }

  /** 上传单文件（multipart/form-data），field 默认 'file' */
  async upload<T = any>(url: string, file: File, field = 'file'): Promise<T> {
    const form = new FormData();
    form.append(field, file);
    return this.client.post(url, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as Promise<T>;
  }
}

export const apiService = new ApiService();
